/**
 * DÓNDE SE DIBUJA CADA CHINCHETA cuando varias caen en el mismo sitio.
 *
 * Tres partes del mismo sector sin línea comparten el centroide EXACTO: no es
 * que estén cerca, es que están en el mismo punto, y por ahí no hay zoom que
 * los separe. Antes se resolvía fusionándolos en un pin con un «3» — y con eso
 * tocarlo abría solo el último, así que los otros dos no existían para el mapa.
 *
 * Ahora se apilan como una baraja: el más reciente en su sitio de verdad y los
 * de detrás asomando hacia arriba, cada uno tocable por su cuenta (petición del
 * usuario 31-08-2026: «uno encima de otro, un poco separados»).
 *
 * EN PÍXELES, NO EN METROS. El desplazamiento se multiplica por `mpp`, así que
 * la baraja se ve igual a cualquier zoom — como el radio de los propios pines.
 * Si fuera en metros, de cerca los partes saldrían a un campo de distancia y de
 * lejos otra vez pegados, que es justo lo que no se quiere.
 *
 * QUIEN DIBUJA Y QUIEN TOCA USAN ESTO MISMO, y no es opcional: el hit-test
 * medía contra el ancla real, así que en cuanto una chincheta se dibuja
 * desplazada deja de responder donde se ve. Una sola función para las dos cosas
 * o vuelve el bug de «unas veces abre y otras no».
 *
 * SIN TOPE. La pila más alta de producción es de 7 y la media 1,33; siete
 * cartas son 78 px de columna, que en un mapa de 400 px caben y se tocan. Un
 * «+4» escondería justo lo que se ha venido a enseñar.
 */
import type { Focus, TreatmentMark } from "./farmHealthModel";

/** Separación entre cartas, en píxeles de pantalla. */
export const STACK_STEP_PX = 13;
/** Un poco de deriva lateral: sin ella la pila parece un pin estirado. */
export const STACK_DRIFT_PX = 3;
/**
 * Más cerca que esto en pantalla y son la misma pila.
 *
 * Es lo que hace que al alejarse se junten y al acercarse se separen: dos
 * partes en las líneas 2 y 5 están a 4,4 m, o sea a 44 px con el mapa cerca
 * —dos pilas distintas, cada una sobre su línea— y a 4 px con la finca entera
 * en pantalla, donde se leen como una sola.
 */
export const GROUP_PX = 18;

export interface PlacedFocus {
  focus: Focus;
  /** Dónde se PINTA y dónde se TOCA (local de la nave, ya con el desplazamiento). */
  x: number;
  y: number;
  /** 0 = el de delante, en el ancla real. */
  index: number;
  /** Cuántos hay en su pila. 1 = va solo. */
  size: number;
}

/** Más reciente primero; a igualdad, por id para que no baile entre renders. */
const byRecency = (a: Focus, b: Focus) =>
  a.date === b.date ? (a.id < b.id ? -1 : 1) : a.date < b.date ? 1 : -1;

/**
 * Coloca los focos de UNA nave. `mpp` = metros por píxel del mapa.
 *
 * Devuelve las cartas en ORDEN DE PINTADO: las de atrás primero, así que quien
 * dibuje puede recorrer el array tal cual y el más reciente queda encima.
 */
export function layoutFoci(foci: Focus[], mpp: number): PlacedFocus[] {
  if (foci.length === 0) return [];
  /* SIN UNA ESCALA VÁLIDA, NADA SE MUEVE.
     `mpp` llega desde el mapa y hay caminos donde todavía es 0 —el primer
     fotograma, antes de medir el contenedor— o `undefined` si alguien llama a
     `hitTestModel` con la firma vieja. Sin esto, el desplazamiento sale `NaN`,
     `Math.hypot` sale `NaN`, y `NaN <= tol` es false: el mapa dejaría de
     responder al dedo SIN dar ningún error. Degradar a «todos en su ancla» es
     exactamente el comportamiento de antes de la baraja. */
  const step = Number.isFinite(mpp) && mpp > 0 ? mpp : 0;
  const sorted = [...foci].sort(byRecency);

  /* Agrupación glotona sobre las ANCLAS reales. Es O(n²) sobre los focos de una
     nave —27 anclas en toda la base de producción— y a cambio es estable y se
     lee de una pasada. */
  const groupRadius = GROUP_PX * step;
  const groups: Focus[][] = [];
  for (const f of sorted) {
    const home = groups.find((g) => Math.hypot(g[0].x - f.x, g[0].y - f.y) <= groupRadius);
    if (home) home.push(f);
    else groups.push([f]);
  }

  const out: PlacedFocus[] = [];
  for (const g of groups) {
    /* El ANCLA de la pila es la del más reciente, que es el que se queda en su
       sitio exacto. Los de detrás se apartan hacia arriba —`y` crece hacia el
       sur— y un pelín a la derecha. */
    const head = g[0];
    for (let i = g.length - 1; i >= 0; i--) {
      out.push({
        focus: g[i],
        x: head.x + i * STACK_DRIFT_PX * step,
        y: head.y - i * STACK_STEP_PX * step,
        index: i,
        size: g.length,
      });
    }
  }
  return out;
}

/**
 * El foco tocado, midiendo contra la posición DIBUJADA.
 *
 * Gana el centro más cercano: como las cartas van a `STACK_STEP_PX` una de
 * otra, cada una se queda con su franja y todas se pueden abrir.
 */
export function pickFocusAt(
  placed: PlacedFocus[],
  local: { x: number; y: number },
  tolM: number,
): PlacedFocus | null {
  let best: { p: PlacedFocus; d: number } | null = null;
  for (const p of placed) {
    const d = Math.hypot(p.x - local.x, p.y - local.y);
    if (d <= tolM && (!best || d < best.d)) best = { p, d };
  }
  return best?.p ?? null;
}

/**
 * Lo mismo para las pulverizaciones, pero la baraja cae HACIA ABAJO.
 *
 * Comparten sitio con los partes sin línea —las dos cosas se anclan al
 * centroide del sector— así que si se apilaran igual saldrían unas encima de
 * otras y no habría forma de saber qué es qué. Los partes suben, los
 * tratamientos bajan: dos columnas desde el mismo punto, que además se lee como
 * lo que es —lo reportado arriba, lo hecho abajo—.
 *
 * Y la FORMA también las separa: los partes son círculos y los tratamientos
 * cuadrados redondeados (ver `TreatmentMarkers`). Con el color solo no bastaba,
 * porque un parte tratado ya es verde.
 *
 * EL DESPLAZAMIENTO DE BASE NO ES OPCIONAL, y esto costó un bug reportado
 * (31-08-2026). Que cada familia se abanique en un sentido separa las cartas
 * DENTRO de una pila, pero no las dos familias entre sí: un tratamiento solo y
 * un parte solo son los dos `index = 0`, o sea los dos en el centroide exacto,
 * o sea el mismo píxel. Encima el parte se pinta después y se comprueba antes,
 * así que la pulverización quedaba debajo y no se podía abrir de ninguna manera
 * — se veía y no respondía.
 * `TREATMENT_BASE_PX` separa la primera carta de las dos familias siempre. Es
 * mayor que el radio del pin más gordo (12 px) más el medio lado del cuadrado
 * (9 px), así que no llegan ni a tocarse.
 */
export const TREATMENT_BASE_PX = 24;
export interface PlacedTreatment {
  treatment: TreatmentMark;
  x: number;
  y: number;
  index: number;
  size: number;
}

export function layoutTreatments(marks: TreatmentMark[], mpp: number): PlacedTreatment[] {
  if (marks.length === 0) return [];
  const step = Number.isFinite(mpp) && mpp > 0 ? mpp : 0;
  // Más reciente primero, y por id a igualdad de fecha: mismo criterio que los
  // partes, para que la pila no baile entre renders.
  const sorted = [...marks].sort((a, b) =>
    a.date === b.date ? (a.id < b.id ? -1 : 1) : a.date < b.date ? 1 : -1,
  );

  const groupRadius = GROUP_PX * step;
  const groups: TreatmentMark[][] = [];
  for (const m of sorted) {
    const home = groups.find((g) => Math.hypot(g[0].x - m.x, g[0].y - m.y) <= groupRadius);
    if (home) home.push(m);
    else groups.push([m]);
  }

  const out: PlacedTreatment[] = [];
  for (const g of groups) {
    const head = g[0];
    for (let i = g.length - 1; i >= 0; i--) {
      out.push({
        treatment: g[i],
        x: head.x + i * STACK_DRIFT_PX * step,
        // `y` crece hacia el sur: SUMAR es bajar. El desplazamiento de base va
        // aunque `i` sea 0 — ver la cabecera: es lo que impide que la primera
        // carta caiga encima de un parte anclado al mismo centroide.
        y: head.y + TREATMENT_BASE_PX * step + i * STACK_STEP_PX * step,
        index: i,
        size: g.length,
      });
    }
  }
  return out;
}

/** El tratamiento tocado, midiendo contra la posición dibujada. */
export function pickTreatmentAt(
  placed: PlacedTreatment[],
  local: { x: number; y: number },
  tolM: number,
): PlacedTreatment | null {
  let best: { p: PlacedTreatment; d: number } | null = null;
  for (const p of placed) {
    const d = Math.hypot(p.x - local.x, p.y - local.y);
    if (d <= tolM && (!best || d < best.d)) best = { p, d };
  }
  return best?.p ?? null;
}
